import { ShipBuilderState } from "../../types/ship";
import { CalculationResult } from "../../types/calculations";
import { calculateBridgeWeight } from "../../constants/bridges";
import { SENSORS } from "../../constants/sensors";

/**
 * Calculates total weight including all components
 * @param state - Ship builder state
 * @param weaponCounts - Weapon counts
 * @param optionalSystems - Optional systems data (from separate slice)
 * @returns Calculation result with total weight or error
 */
export const calculateTotalWeight = (
  state: ShipBuilderState,
  weaponCounts: Record<string, number>,
  optionalSystems?: any,
): CalculationResult<number> => {
  try {
    // Use the consolidated selector instead of recalculating
    // This ensures consistency with other parts of the application
    const { selectTotalUsedCargoTonnage } = require("../../store/selectors/derivedSelectors");
    
    // Create a mock state object for the selector
    const mockState = {
      shipBuilder: state,
      optionalSystems: optionalSystems || {},
    };
    
    const totalWeight = selectTotalUsedCargoTonnage(mockState);
    
    return {
      success: true,
      data: totalWeight,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate total weight: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};

/**
 * Calculates power requirements for all systems
 * @param state - Ship builder state
 * @param weaponCounts - Weapon counts
 * @returns Calculation result with power requirements or error
 */
export const calculatePowerRequired = (
  state: ShipBuilderState,
  weaponCounts: { [weaponType: string]: number },
): CalculationResult<number> => {
  try {
    let powerRequired = 0;

    console.log("calculatePowerRequired: Starting calculation");
    console.log("calculatePowerRequired: shipSize:", state.shipSize);

    // Basic systems power (20% of ship size per Traveller rules)
    const basicSystemsPower = (state.shipSize || 0) * 0.2;
    powerRequired += basicSystemsPower;
    console.log("calculatePowerRequired: basicSystemsPower:", basicSystemsPower);

    // Add drive power requirements according to Traveller rules (only use primary drives)
    if (state.drives) {
      // Maneuver drive: 10% of hull tonnage × thrust rating (only use primary drive for power)
      const maneuverDrive = state.drives.find((drive) => drive.type === "maneuver" && drive.role === 'primary');
      if (maneuverDrive) {
        const thrustMultiplier = maneuverDrive.rating === 0 ? 0.25 : 1;
        const maneuverDrivePower = state.shipSize * 0.1 * maneuverDrive.rating * thrustMultiplier;
        powerRequired += maneuverDrivePower;
        console.log("calculatePowerRequired: maneuverDrivePower:", maneuverDrivePower);
      }
      
      // Jump drive: 10% of hull tonnage × jump rating (only use primary drive for power)
      const jumpDrive = state.drives.find((drive) => drive.type === "jump" && drive.role === 'primary');
      if (jumpDrive) {
        const jumpDrivePower = state.shipSize * 0.1 * jumpDrive.rating;
        powerRequired += jumpDrivePower;
        console.log("calculatePowerRequired: jumpDrivePower:", jumpDrivePower);
      }
    }
    // Remove the incorrect line: if (state.powerPlant?.power) powerRequired += state.powerPlant.power;
    if (state.weaponMounts) {
      const { power: weaponMountsPower } =
        require("./weaponCalculations").calculateWeaponMountsTotals(
          state.weaponMounts,
        );
      powerRequired += weaponMountsPower || 0;
      console.log("calculatePowerRequired: weaponMountsPower:", weaponMountsPower || 0);
    }
    if (state.defensiveSystems) {
      const { power: defensiveSystemsPower } =
        require("./weaponCalculations").calculateDefensiveSystemsTotals(
          state.defensiveSystems,
        );
      powerRequired += defensiveSystemsPower || 0;
      console.log("calculatePowerRequired: defensiveSystemsPower:", defensiveSystemsPower || 0);
    }
    if (state.optionalSystems) {
      const optionalSystemsPower = state.optionalSystems.reduce(
        (sum, system) => sum + (system.power || 0),
        0,
      );
      powerRequired += optionalSystemsPower;
      console.log("calculatePowerRequired: optionalSystemsPower:", optionalSystemsPower);
    }
    
    // Calculate bridge power
    if (state.bridge) {
      const bridgePower = state.bridge.power || 0;
      powerRequired += bridgePower;
      console.log("calculatePowerRequired: bridgePower:", bridgePower);
    }
    
    // Calculate sensors power
    if (state.sensors) {
      const sensorsPower = state.sensors.power || 0;
      powerRequired += sensorsPower;
      console.log("calculatePowerRequired: sensorsPower:", sensorsPower);
    }
    
    // Calculate stateroom power
    if (state.stateroomConfig) {
      const stateroomsPower = 
        // Staterooms power (0 for standard)
        (state.stateroomConfig.single || 0) * 0 + // Standard staterooms have 0 power
        (state.stateroomConfig.double || 0) * 0 + // Standard staterooms have 0 power
        // Low berths power (1 per 10 berths)
        Math.ceil((state.stateroomConfig.lowBerths?.count || 0) / 10) +
        // Emergency low berths power
        (state.stateroomConfig.emergencyLowBerths?.count || 0) * 1 + // 1 power each
        // Common area power
        (state.stateroomConfig.commonArea || 0) * 0.5; // 0.5 power per ton
      powerRequired += stateroomsPower;
      console.log("calculatePowerRequired: stateroomsPower:", stateroomsPower);
    }

    console.log("calculatePowerRequired: Final powerRequired:", powerRequired);

    return {
      success: true,
      data: powerRequired,
    };
  } catch (error) {
    console.error("calculatePowerRequired: Error:", error);
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate power requirements: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};

/**
 * Calculates bridge size based on ship size
 * @param shipSize - The ship size in tons
 * @returns The bridge size in tons
 */
export const calculateBridgeSize = (shipSize: number): number => {
  // Use the Traveller ruleset-compliant bridge weight calculation
  return calculateBridgeWeight("STANDARD", shipSize);
};

/**
 * Calculates sensors cost based on type
 * @param type - Sensor type
 * @returns The sensors cost in credits
 */
export const calculateSensorsCost = (type: string): number => {
  const sensor = SENSORS[type as keyof typeof SENSORS];
  return sensor ? sensor.cost : 0;
};

/**
 * Calculates sensors weight based on type
 * @param type - Sensor type
 * @returns The sensors weight in tons
 */
export const calculateSensorsTons = (type: string): number => {
  const sensor = SENSORS[type as keyof typeof SENSORS];
  return sensor ? sensor.tons : 0;
};

/**
 * Calculates the cost of cargo based on cargo selection
 * @param cargoSelection - The cargo selection configuration
 * @returns The total cost of the cargo
 */
export const calculateCargoCost = (cargoSelection: any): number => {
  if (!cargoSelection) return 0;

  // If cargoSelection has a cost property, use it
  if (typeof cargoSelection.cost === "number") {
    return cargoSelection.cost;
  }

  // If cargoSelection has tons and costPerTon properties, calculate cost
  if (
    typeof cargoSelection.tons === "number" &&
    typeof cargoSelection.costPerTon === "number"
  ) {
    return cargoSelection.tons * cargoSelection.costPerTon;
  }

  // Default to 0 if no cost information is available
  return 0;
};
